import NextApp, {AppInitialProps, AppProps} from 'next/app'
import {useEffect, useLayoutEffect, useState} from 'react'
import {SWRConfig} from 'swr'
import constate from 'constate'
import useEventListener from '@/hooks/useEventListener'
import initOpenAppSDK from '@/plugins/initOpenAppSDK'
import initSentry from '@/plugins/initSentry'
import initVConsole from '@/plugins/initConsole'
import '@/styles/global.less'
// import 'uno.css';
import 'gotu-ui/es/global'
import {hideNavigationStatusBar, notifyPage} from '@/plugins/bridge'
import {haboPerf} from '@/plugins/habo'
import CustomHead from '@/components/CustomHead'
import {CUSTOM_HEAD_TITLE, SUCCESS_CODE} from '@/constants'
import TitleBar from '@/components/TitleBar'
import CustomError from '@/components/CustomError'
import CancelAppLoading from '@/components/CancelAppLoading'
import Polyfill from '@/components/Polyfill'
import getUaParam, {IUaParam} from '@/utils/getUaParam'
import '@gaotu-fe/exercise/dist/index.css'
import {laggy} from '@/plugins/swrPlugin'
import getErrorInfoFromCode from '@/components/CustomError/getErrorInfoFromCode'
import {APIError} from '@/constants/error'
import {parse} from 'cookie'
import {LoginSchemeProps} from './video/[mediaID]'
import Upgrade from './upgrade'

export const [Provider, useGlobal, useUaParam] = constate(
    (props: {
        renderId: string;
        uaParam: IUaParam;
    }) => {
        const [isNextTick, setIsNextTick] = useState(false)
        useEffect(() => {
            setIsNextTick(true)
        }, [])
        const [uaParam, setUaParam] = useState<IUaParam>(props.uaParam)
        const refreshUaParam = () => {
            const uaParam = getUaParam()
            setUaParam(uaParam)
        }
        const [loginSchemeParams, setLoginSchemeParams] = useState<LoginSchemeProps>({need_login: false})
        const globalInfo = {
            renderId: props.renderId,
            uaParam,
            refreshUaParam,
            isNextTick,
            loginSchemeParams,
            setLoginSchemeParams,
        }
        return globalInfo
    },
    (global) => global,
    ({uaParam}) => uaParam
)

const App = (appProps: AppProps & {ua: string; renderId: string; serverError: any}) => {
    const {
        Component,
        pageProps,
        router,
        ua,
        renderId,
        serverError,
    } = appProps
    const {
        code = APIError.customError,
        fallback = {},
        isShowNavigationStatusBar = true,
        isCancelAppLoading = true,
        errorTitle = CUSTOM_HEAD_TITLE,
        reason = 'JS执行出错',
        otherInfo = serverError,
        appName,
    } = pageProps
    const uaParam = getUaParam(router.asPath, ua)
    const specifiedCode = +uaParam.errorCode || code
    const props = {renderId, ...pageProps}
    useLayoutEffect(() => {
        // 初始化openAppSdk
        initOpenAppSDK(appName)
    }, [])
    useEffect(() => {
        // 初始化sentry
        initSentry()
        // 初始化vConsole
        initVConsole()
        // 隐藏状态栏
        isShowNavigationStatusBar && hideNavigationStatusBar()
        // 上报fmp, 作为对比
        haboPerf()
        // 更新缓存
        notifyPage()
        // 上报是否强制登录
        const cookies = parse(document.cookie)
        const {sid, GID} = cookies
        if (sid && GID) {
            // 上报
        }
        // 显示fallback
        console.log('fallback ===> ', fallback)
        return () => {
            // 页面数据可能会刷新, 结束时再刷新一次缓存, 但对于ios的最后一个页面, 不会执行
            notifyPage()
        }
    }, [])
    // 支持 ios ::active
    useEventListener('touchstart', () => { }, () => window)

    if (specifiedCode !== SUCCESS_CODE) {
        const info = getErrorInfoFromCode(specifiedCode, uaParam, Object.values(fallback as IObject)[0]?.message)
        return (
            <Provider
                renderId={renderId}
                uaParam={uaParam}
            >
                <CancelAppLoading />
                <Polyfill />
                <CustomHead title={errorTitle} />
                {isShowNavigationStatusBar && (<TitleBar />)}
                <CustomError
                    reason={reason}
                    otherInfo={otherInfo || fallback}
                    {...info}
                />
            </Provider>
        )
    }
    // 需要升级app
    if (code === APIError.customUpgrade) {
        return (
            <Provider
                renderId={renderId}
                uaParam={uaParam}
            >
                <CancelAppLoading />
                <Polyfill />
                <Upgrade hiddenTitleBar={true}></Upgrade>
            </Provider>
        )
    }
    return (
        <SWRConfig value={{
            fallback,
            use: [laggy],
        }}
        >
            <Provider
                renderId={renderId}
                uaParam={uaParam}
            >
                {isCancelAppLoading && (<CancelAppLoading />)}
                <Polyfill />
                <CustomHead title={props.title} />
                <Component {...props} />
            </Provider>
        </SWRConfig>
    )
}

App.getInitialProps = async (appContext: any) => {
    const {
        err,
        req,
        res,
    } = appContext.ctx
    let serverError = err?.stack
    if (serverError) {
        res.statusCode = 200
    }
    const {pageProps} = await NextApp.getInitialProps(appContext)
    const ua = req.headers['user-agent']
    const trafficEnv = req.headers['traffic-env']
    const {renderId} = req.headers
    return {
        pageProps,
        ua,
        trafficEnv,
        renderId,
        serverError,
        err: {},
    }
}
export default App
